import { useState, useCallback, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Plus, X, Zap, Star, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ReportField, ReportFilter, QuickFilter, DateRangePreset, ReportAction } from './types'
import {
  OPERATORS,
  QUICK_FILTERS,
  DATE_RANGE_PRESETS,
  COMPARABLE_PERIODS,
  formatDate,
} from './types'

interface FilterBuilderProps {
  dataSource: string
  fields: ReportField[]
  filters: ReportFilter[]
  currentDateField: string
  selectedDateRange: string
  customStartDate: string
  customEndDate: string
  comparisonEnabled: boolean
  comparisonPeriod: 'previous' | 'year_ago'
  activeQuickFilters: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  dispatch: (action: ReportAction) => void
  onDateFieldChange: (field: string) => void
  onDateRangeChange: (range: string) => void
  onCustomStartDateChange: (date: string) => void
  onCustomEndDateChange: (date: string) => void
  onComparisonEnabledChange: (enabled: boolean) => void
  onComparisonPeriodChange: (period: 'previous' | 'year_ago') => void
  onActiveQuickFiltersChange: (filters: string[]) => void
}

function FilterPill({
  filter,
  fields,
  onUpdate,
  onRemove,
}: {
  filter: ReportFilter
  fields: ReportField[]
  onUpdate: (updates: Partial<ReportFilter>) => void
  onRemove: () => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const fieldLabel = fields.find((f) => f.name === filter.field)?.label || filter.field
  const operatorLabel = OPERATORS.find((o) => o.value === filter.operator)?.label || filter.operator
  const displayValue = filter.value
    ? filter.value.length > 20
      ? filter.value.substring(0, 20) + '...'
      : filter.value
    : ''

  if (isEditing) {
    return (
      <div
        className="inline-flex items-center gap-2 p-3 bg-muted rounded-lg border"
        role="group"
        aria-label="Edit filter"
      >
        <Select value={filter.field} onValueChange={(v) => onUpdate({ field: v })}>
          <SelectTrigger className="h-8 w-36" aria-label="Select field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fields.map((f) => (
              <SelectItem key={f.id} value={f.name}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.operator} onValueChange={(v) => onUpdate({ operator: v })}>
          <SelectTrigger className="h-8 w-32" aria-label="Select operator">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filter.operator !== 'is_null' && filter.operator !== 'is_not_null' && (
          <Input
            value={filter.value}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className="h-8 w-28"
            placeholder="Value"
            aria-label="Filter value"
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={() => setIsEditing(false)}
          aria-label="Done editing"
        >
          Done
        </Button>
      </div>
    )
  }

  return (
    <Badge
      variant="secondary"
      className="cursor-pointer hover:bg-secondary/80 gap-1.5 py-1.5 px-3 text-sm"
      onClick={() => setIsEditing(true)}
      role="button"
      tabIndex={0}
      aria-label={`Edit filter: ${fieldLabel} ${operatorLabel} ${displayValue}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsEditing(true)
        }
      }}
    >
      <span className="font-medium">{fieldLabel}</span>
      <span className="text-muted-foreground">{operatorLabel}</span>
      {displayValue && <span className="text-primary">&quot;{displayValue}&quot;</span>}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className="ml-1 hover:text-destructive rounded-sm focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={`Remove filter: ${fieldLabel}`}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </Badge>
  )
}

export function FilterBuilder({
  dataSource,
  fields,
  filters,
  currentDateField,
  selectedDateRange,
  customStartDate,
  customEndDate,
  comparisonEnabled,
  comparisonPeriod,
  activeQuickFilters,
  open,
  onOpenChange,
  dispatch,
  onDateFieldChange,
  onDateRangeChange,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onComparisonEnabledChange,
  onComparisonPeriodChange,
  onActiveQuickFiltersChange,
}: FilterBuilderProps) {
  const availableQuickFilters = useMemo(() => QUICK_FILTERS[dataSource] || [], [dataSource])
  const dateFields = useMemo(
    () => fields.filter((f) => f.type === 'date').map((f) => ({ name: f.name, label: f.label })),
    [fields]
  )

  const toggleQuickFilter = useCallback(
    (quickFilter: QuickFilter) => {
      const isActive = activeQuickFilters.includes(quickFilter.id)
      if (isActive) {
        onActiveQuickFiltersChange(activeQuickFilters.filter((id) => id !== quickFilter.id))
        const filterFieldsToRemove = quickFilter.filters.map((f) => f.field)
        dispatch({
          type: 'SET_FILTERS',
          filters: filters.filter((f) => !filterFieldsToRemove.includes(f.field)),
        })
      } else {
        onActiveQuickFiltersChange([...activeQuickFilters, quickFilter.id])
        let filtersToAdd = quickFilter.filters
        if (quickFilter.id === 'closing-soon') {
          const now = new Date()
          const start = new Date(now.getFullYear(), now.getMonth(), 1)
          const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
          filtersToAdd = [
            { field: 'close_date', operator: 'greater_or_equal', value: formatDate(start) },
            { field: 'close_date', operator: 'less_or_equal', value: formatDate(end) },
          ]
        }
        const newFilters: ReportFilter[] = filtersToAdd.map((f, i) => ({
          id: `quick-${quickFilter.id}-${i}-${Date.now()}`,
          field: f.field,
          operator: f.operator,
          value: f.value,
        }))
        dispatch({ type: 'ADD_FILTERS', filters: newFilters })
      }
    },
    [activeQuickFilters, filters, dispatch, onActiveQuickFiltersChange]
  )

  const applyDateRange = (preset: DateRangePreset) => {
    if (!currentDateField) return
    const range = preset.getRange()
    const otherFilters = filters.filter((f) => f.field !== currentDateField)
    dispatch({
      type: 'SET_FILTERS',
      filters: [
        ...otherFilters,
        {
          id: `filter-date-start-${Date.now()}`,
          field: currentDateField,
          operator: 'greater_or_equal',
          value: range.start,
        },
        {
          id: `filter-date-end-${Date.now()}`,
          field: currentDateField,
          operator: 'less_or_equal',
          value: range.end,
        },
      ],
    })
    onDateRangeChange(preset.value)
  }

  const applyCustomDateRange = () => {
    if (!currentDateField || !customStartDate || !customEndDate) return
    const otherFilters = filters.filter((f) => f.field !== currentDateField)
    dispatch({
      type: 'SET_FILTERS',
      filters: [
        ...otherFilters,
        {
          id: `date-start-${Date.now()}`,
          field: currentDateField,
          operator: 'greater_or_equal',
          value: customStartDate,
        },
        {
          id: `date-end-${Date.now() + 1}`,
          field: currentDateField,
          operator: 'less_or_equal',
          value: customEndDate,
        },
      ],
    })
    onDateRangeChange('custom')
  }

  const clearDateFilter = () => {
    dispatch({ type: 'SET_FILTERS', filters: filters.filter((f) => f.field !== currentDateField) })
    onDateRangeChange('')
    onCustomStartDateChange('')
    onCustomEndDateChange('')
  }

  const addFilter = () => {
    const newFilter: ReportFilter = {
      id: `filter-${Date.now()}`,
      field: fields[0]?.name || '',
      operator: 'equals',
      value: '',
    }
    dispatch({ type: 'ADD_FILTER', filter: newFilter })
  }

  return (
    <>
      {/* Date Range Bar */}
      {dataSource && dateFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Label htmlFor="date-field-top" className="text-sm font-medium whitespace-nowrap">
              Date Filter:
            </Label>
          </div>
          <Select value={currentDateField} onValueChange={onDateFieldChange}>
            <SelectTrigger id="date-field-top" className="w-[140px] h-8">
              <SelectValue placeholder="Date field" />
            </SelectTrigger>
            <SelectContent>
              {dateFields.map((field) => (
                <SelectItem key={field.name} value={field.name}>
                  {field.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1">
            {DATE_RANGE_PRESETS.slice(0, 4).map((preset) => (
              <Button
                key={preset.value}
                variant={selectedDateRange === preset.value ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => applyDateRange(preset)}
                disabled={!currentDateField}
                aria-pressed={selectedDateRange === preset.value}
              >
                {preset.label}
              </Button>
            ))}
            <Select
              value={
                DATE_RANGE_PRESETS.slice(4).some((p) => p.value === selectedDateRange)
                  ? selectedDateRange
                  : ''
              }
              onValueChange={(value) => {
                const preset = DATE_RANGE_PRESETS.find((p) => p.value === value)
                if (preset) applyDateRange(preset)
              }}
            >
              <SelectTrigger className="h-7 w-[100px] text-xs" disabled={!currentDateField}>
                <SelectValue placeholder="More..." />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_PRESETS.slice(4).map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">or</span>
            <DatePicker
              value={customStartDate}
              onChange={(v) => onCustomStartDateChange(v ?? '')}
              className="h-7 w-[130px] text-xs"
              disabled={!currentDateField}
              aria-label="Start date"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker
              value={customEndDate}
              onChange={(v) => onCustomEndDateChange(v ?? '')}
              className="h-7 w-[130px] text-xs"
              disabled={!currentDateField}
              aria-label="End date"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={applyCustomDateRange}
              disabled={!currentDateField || !customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </div>
          {(selectedDateRange || customStartDate) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={clearDateFilter}
              aria-label="Clear date filter"
            >
              <X className="h-3 w-3 mr-1" aria-hidden="true" />
              Clear
            </Button>
          )}
          {selectedDateRange && COMPARABLE_PERIODS.includes(selectedDateRange) && (
            <div className="flex items-center gap-2 ml-auto pl-3 border-l">
              <Switch
                id="period-comparison-top"
                checked={comparisonEnabled}
                onCheckedChange={onComparisonEnabledChange}
                aria-label="Enable period comparison"
              />
              <Label
                htmlFor="period-comparison-top"
                className="text-xs whitespace-nowrap cursor-pointer"
              >
                Compare to
              </Label>
              <Select
                value={comparisonPeriod}
                onValueChange={(v) => onComparisonPeriodChange(v as 'previous' | 'year_ago')}
                disabled={!comparisonEnabled}
              >
                <SelectTrigger className="h-7 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="previous">Previous Period</SelectItem>
                  <SelectItem value="year_ago">Same Period Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Filters Card */}
      <Card>
        <Collapsible open={open} onOpenChange={onOpenChange}>
          <CollapsibleTrigger className="w-full" disabled={!dataSource}>
            <CardHeader
              className={cn(
                'flex flex-row items-center justify-between p-4 cursor-pointer rounded-t-lg',
                dataSource ? 'hover:bg-muted/50' : 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Filters</CardTitle>
                {filters.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {filters.length}
                  </Badge>
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {availableQuickFilters.length > 0 && (
                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="h-4 w-4" aria-hidden="true" />
                    Quick Filters
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableQuickFilters.map((quickFilter) => {
                      const isActive = activeQuickFilters.includes(quickFilter.id)
                      const IconComponent =
                        quickFilter.icon === 'star'
                          ? Star
                          : quickFilter.icon === 'calendar'
                            ? Calendar
                            : Zap
                      return (
                        <Button
                          key={quickFilter.id}
                          variant={isActive ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleQuickFilter(quickFilter)}
                          aria-pressed={isActive}
                          aria-label={`${isActive ? 'Remove' : 'Apply'} ${quickFilter.label} filter`}
                        >
                          <IconComponent className="mr-2 h-3 w-3" aria-hidden="true" />
                          {quickFilter.label}
                          {isActive && <X className="ml-2 h-3 w-3" aria-hidden="true" />}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Add filters to narrow down results</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addFilter}
                  disabled={fields.length === 0}
                  aria-label="Add new filter"
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Add Filter
                </Button>
              </div>

              {filters.length === 0 ? (
                <div className="p-6 text-center border-2 border-dashed rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    No filters added. Click &quot;Add Filter&quot; or use quick filters above.
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" role="list" aria-label="Active filters">
                  {filters.map((filter) => (
                    <FilterPill
                      key={filter.id}
                      filter={filter}
                      fields={fields}
                      onUpdate={(updates) =>
                        dispatch({ type: 'UPDATE_FILTER', filterId: filter.id, updates })
                      }
                      onRemove={() => dispatch({ type: 'REMOVE_FILTER', filterId: filter.id })}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </>
  )
}
